export function isStringRecord(value) {
    return typeof value === 'object' && value !== null;
}
export function hasPushRequestUri(data) {
    return isStringRecord(data) && typeof data['request_uri'] === 'string';
}
export function buildAuthorizeOptions(wellknown, config, options) {
    const isPiFlow = wellknown.response_modes_supported?.includes('pi.flow');
    return [
        wellknown.authorization_endpoint,
        {
            clientId: config.clientId,
            redirectUri: config.redirectUri,
            scope: config.scope || 'openid',
            responseType: config.responseType || 'code',
            ...(isPiFlow && { responseMode: 'pi.flow' }),
            ...options,
        },
    ];
}
/**
 * Type guard for RTK FetchBaseQueryError vs SerializedError.
 * FetchBaseQueryError always carries a `status` field; SerializedError does not.
 */
export function isFetchBaseQueryError(error) {
    return 'status' in error;
}
const KNOWN_ERROR_TYPES = new Set([
    'auth_error',
    'argument_error',
    'network_error',
    'unknown_error',
    'wellknown_error',
]);
function isKnownErrorType(value) {
    return typeof value === 'string' && KNOWN_ERROR_TYPES.has(value);
}
/**
 * Safely narrows an unknown value to AuthorizationError shape.
 * Validates that the data has the required 'error' string field, otherwise returns
 * a default unknown error response.
 */
export function toAuthorizationError(data) {
    if (isStringRecord(data)) {
        if (typeof data['error'] === 'string') {
            return {
                error: data['error'],
                error_description: typeof data['error_description'] === 'string' ? data['error_description'] : '',
                type: isKnownErrorType(data['type']) ? data['type'] : 'unknown_error',
                ...(typeof data['redirectUrl'] === 'string' && { redirectUrl: data['redirectUrl'] }),
            };
        }
    }
    return {
        error: 'Unknown_Error',
        error_description: 'Unexpected error response shape',
        type: 'unknown_error',
    };
}
/**
 * Constructs the slim PAR authorize URL containing only client_id and request_uri
 * (and optionally prompt). Keeping sensitive params out of the browser address bar
 * is the core security value of PAR (RFC 9126).
 */
export function buildParAuthorizeUrl({ authorizationEndpoint, clientId, requestUri, prompt, }) {
    const url = new URL(authorizationEndpoint);
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('request_uri', requestUri);
    if (prompt)
        url.searchParams.set('prompt', prompt);
    return url.toString();
}
/**
 * Converts an RTK Query dispatch error to a typed AuthorizationError.
 * oidc.api.ts normalizes all FetchBaseQueryErrors so that error.data always
 * contains { error, error_description, type }. SerializedErrors fall back to
 * their code/message fields.
 */
export function toDispatchError(error) {
    if (!isFetchBaseQueryError(error)) {
        return {
            error: error.code ?? 'Unknown_Error',
            error_description: error.message ?? 'An unknown error occurred during authorization',
            type: 'unknown_error',
        };
    }
    return toAuthorizationError(error.data);
}
//# sourceMappingURL=authorize.request.utils.js.map